/* Sections of one screen, switched in place.
 *
 * The markup and class names are exactly those in `design/components/tabs` —
 * the mockup is the specification, and keeping them identical is what lets the
 * two be diffed.
 *
 * ── the CSS names the contract this file owes ───────────────────────────────
 * `tabs.css` says it outright: carrying `role="tablist"` advertises a keyboard
 * contract — arrow keys move between tabs, Home and End jump to the ends — and
 * CSS cannot supply it. A tablist without that behaviour is worse than a row of
 * buttons, because it has promised something. So it is implemented here, and
 * this component exists rather than the markup being written per screen.
 *
 * ── the panel takes focus, not the tab ──────────────────────────────────────
 * `tabIndex={0}` on the panel is deliberate: the panel is a region with no
 * other guaranteed focusable entry point, and without it a keyboard user tabs
 * from the tab straight past everything the tab just revealed.
 *
 * ── only the selected tab is in the tab order ───────────────────────────────
 * A roving tabindex, which is what the pattern asks for: one Tab press reaches
 * the tablist and the arrows move within it, rather than every tab being a
 * separate stop on the way to the content.
 */

import type { ReactNode } from 'react';
import { useId, useRef } from 'react';

export interface TabDefinition {
  readonly id: string;
  readonly label: string;
  /** Shown beside the label — a count of what is inside. Optional. */
  readonly count?: number;
  readonly panel: ReactNode;
}

export interface TabsProps {
  /** Names the tablist for a screen reader. Never omitted. */
  readonly label: string;
  readonly tabs: readonly TabDefinition[];
  readonly selected: string;
  readonly onSelect: (id: string) => void;
}

export function Tabs({ label, tabs, selected, onSelect }: TabsProps) {
  const base = useId();
  const list = useRef<HTMLDivElement>(null);

  const tabId = (id: string) => `${base}-tab-${id}`;
  const panelId = (id: string) => `${base}-panel-${id}`;

  const move = (to: number) => {
    const next = tabs[(to + tabs.length) % tabs.length];
    if (!next) return;
    onSelect(next.id);
    /* Focus follows selection, which is the pattern for tabs whose panels are
     * cheap to render: arrowing shows the panel rather than requiring a second
     * key to activate it. */
    list.current?.querySelector<HTMLButtonElement>(`#${CSS.escape(tabId(next.id))}`)?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const at = tabs.findIndex((tab) => tab.id === selected);
    if (at === -1) return;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        move(at + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        move(at - 1);
        break;
      case 'Home':
        event.preventDefault();
        move(0);
        break;
      case 'End':
        event.preventDefault();
        move(tabs.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <>
      {/* `tablist` has no HTML element of its own — the pattern is a container
          of buttons carrying the role, and the keyboard contract it advertises
          is implemented above, which is what makes the role honest rather than
          decorative. */}
      <div className="tabs" role="tablist" aria-label={label} ref={list} onKeyDown={onKeyDown}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className="tab"
            role="tab"
            id={tabId(tab.id)}
            aria-selected={tab.id === selected}
            aria-controls={panelId(tab.id)}
            /* Roving: only the selected tab is a tab stop. */
            tabIndex={tab.id === selected ? 0 : -1}
            onClick={() => onSelect(tab.id)}
          >
            {tab.label}
            {tab.count === undefined ? null : <span className="tab__count">{tab.count}</span>}
          </button>
        ))}
      </div>

      {tabs.map((tab) => (
        <div
          key={tab.id}
          id={panelId(tab.id)}
          role="tabpanel"
          aria-labelledby={tabId(tab.id)}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: a tabpanel is the one place this rule is wrong. `tabs.css` argues it out: the panel is a region with no guaranteed focusable entry point, so without a stop of its own a keyboard user tabs from the tab straight past everything the tab just revealed. The ARIA pattern requires it.
          tabIndex={0}
          hidden={tab.id !== selected}
        >
          {/* Rendered only when selected. The panels here hold their own
              queries, and mounting them all would fetch every tab's data on
              every visit. `hidden` alone would keep them mounted. */}
          {tab.id === selected ? tab.panel : null}
        </div>
      ))}
    </>
  );
}
